import api from './api';

export interface AuditLog {
  id: string;
  userId: string;
  userEmail: string;
  tenantId: string;
  action: string;
  resource?: string;
  resourceId?: string;
  description: string;
  metadata?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  timestamp: string;
}

export interface AuditLogPage {
  content: AuditLog[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

class AdminAuditService {
  private baseUrl = '/api/admin/audit';

  /**
   * Get recent activities for admin dashboard
   */
  async getRecentActivities(limit: number = 10): Promise<AuditLog[]> {
    try {
      const response = await api.get(`${this.baseUrl}/recent`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch recent activities:', error);
      return [];
    }
  }

  /**
   * Get tenant activities with pagination
   */
  async getTenantActivities(page: number = 0, size: number = 20): Promise<AuditLogPage> {
    try {
      const response = await api.get(`${this.baseUrl}/activities`, {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch tenant activities:', error);
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: 0,
        number: 0
      };
    }
  }

  /**
   * Get user activities with pagination
   */
  async getUserActivities(page: number = 0, size: number = 20): Promise<AuditLogPage> {
    try {
      const response = await api.get(`${this.baseUrl}/user-activities`, {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user activities:', error);
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: 0,
        number: 0
      };
    }
  }

  /**
   * Get activities by action type
   */
  async getActivitiesByAction(action: string, page: number = 0, size: number = 20): Promise<AuditLogPage> {
    try {
      const response = await api.get(`${this.baseUrl}/by-action`, {
        params: { action, page, size }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch activities by action:', error);
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: 0,
        number: 0
      };
    }
  }

  /**
   * Get activity statistics
   */
  async getActivityStats(): Promise<Record<string, any>> {
    try {
      const response = await api.get(`${this.baseUrl}/stats`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch activity stats:', error);
      return {};
    }
  }

  /**
   * Format action for display
   */
  formatAction(action: string): string {
    const actionMap: Record<string, string> = {
      'LOGIN': 'Logged in',
      'LOGOUT': 'Logged out',
      'TEST_CREATE': 'Created test',
      'TEST_UPDATE': 'Updated test',
      'TEST_DELETE': 'Deleted test',
      'TEST_ASSIGN': 'Assigned test',
      'QUESTION_CREATE': 'Created question',
      'QUESTION_UPDATE': 'Updated question',
      'QUESTION_DELETE': 'Deleted question',
      'STUDENT_CREATE': 'Created student',
      'STUDENT_UPDATE': 'Updated student',
      'STUDENT_DELETE': 'Deleted student',
      'CLASS_CREATE': 'Created class',
      'CLASS_UPDATE': 'Updated class',
      'CLASS_DELETE': 'Deleted class',
      'BATCH_CREATE': 'Created batch',
      'BATCH_UPDATE': 'Updated batch',
      'BATCH_DELETE': 'Deleted batch',
      'RESULTS_VIEW': 'Viewed results',
      'ANALYTICS_VIEW': 'Viewed analytics',
      'SETTINGS_UPDATE': 'Updated settings'
    };
    
    return actionMap[action] || action;
  }

  /**
   * Get action icon
   */
  getActionIcon(action: string): string {
    const iconMap: Record<string, string> = {
      'LOGIN': '🔐',
      'LOGOUT': '🚪',
      'TEST_CREATE': '📝',
      'TEST_UPDATE': '✏️',
      'TEST_DELETE': '🗑️',
      'TEST_ASSIGN': '📤',
      'QUESTION_CREATE': '❓',
      'QUESTION_UPDATE': '✏️',
      'QUESTION_DELETE': '🗑️',
      'STUDENT_CREATE': '👤',
      'STUDENT_UPDATE': '✏️',
      'STUDENT_DELETE': '🗑️',
      'CLASS_CREATE': '🏫',
      'CLASS_UPDATE': '✏️',
      'CLASS_DELETE': '🗑️',
      'BATCH_CREATE': '📚',
      'BATCH_UPDATE': '✏️',
      'BATCH_DELETE': '🗑️',
      'RESULTS_VIEW': '📊',
      'ANALYTICS_VIEW': '📈',
      'SETTINGS_UPDATE': '⚙️'
    };
    
    return iconMap[action] || '📝';
  }

  /**
   * Get action color
   */
  getActionColor(action: string): string {
    const colorMap: Record<string, string> = {
      'LOGIN': 'text-green-600',
      'LOGOUT': 'text-gray-600',
      'TEST_CREATE': 'text-blue-600',
      'TEST_UPDATE': 'text-yellow-600',
      'TEST_DELETE': 'text-red-600',
      'TEST_ASSIGN': 'text-purple-600',
      'QUESTION_CREATE': 'text-blue-600',
      'QUESTION_UPDATE': 'text-yellow-600',
      'QUESTION_DELETE': 'text-red-600',
      'STUDENT_CREATE': 'text-green-600',
      'STUDENT_UPDATE': 'text-yellow-600',
      'STUDENT_DELETE': 'text-red-600',
      'CLASS_CREATE': 'text-green-600',
      'CLASS_UPDATE': 'text-yellow-600',
      'CLASS_DELETE': 'text-red-600',
      'BATCH_CREATE': 'text-green-600',
      'BATCH_UPDATE': 'text-yellow-600',
      'BATCH_DELETE': 'text-red-600',
      'RESULTS_VIEW': 'text-indigo-600',
      'ANALYTICS_VIEW': 'text-indigo-600',
      'SETTINGS_UPDATE': 'text-gray-600'
    };
    
    return colorMap[action] || 'text-gray-600';
  }

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }
}

export const adminAuditService = new AdminAuditService();
export default adminAuditService;
